import { useDebounceFn } from "@vueuse/core";
import { computed, onBeforeUnmount, onMounted, onUnmounted, ref, type Ref } from "vue";
import { onBeforeRouteLeave } from "vue-router";
import { z, type ZodType } from "zod";

const DEFAULT_DEBOUNCE_MS = 600;
const DEFAULT_DEBOUNCE_MAX_WAIT_MS = 1500;
const DEFAULT_DRAFT_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000;

const storedDraftEnvelopeSchema = z.object({
  patch: z.unknown(),
  savedAt: z.number(),
});

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;

type AutosaveOptions<Patch extends Record<string, unknown>> = {
  save: (patch: Partial<Patch>) => Promise<unknown>;
  debounceMs?: number;
  maxWaitMs?: number;
  draftBackup?: {
    storageKey: () => string;
    patchSchema: ZodType<Partial<Patch>>;
    expiryMs?: number;
    storage?: StorageLike;
  };
};

type AutosaveResult<Patch extends Record<string, unknown>> = {
  queue: (patch: Partial<Patch>) => void;
  restoreDraft: () => Partial<Patch> | null;
};

export function useAutosave<Patch extends Record<string, unknown>>(
  options: AutosaveOptions<Patch>
): AutosaveResult<Patch> {
  const { save, draftBackup } = options;
  const debounceMs = options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  const maxWaitMs = options.maxWaitMs ?? DEFAULT_DEBOUNCE_MAX_WAIT_MS;
  const expiryMs = draftBackup?.expiryMs ?? DEFAULT_DRAFT_EXPIRY_MS;
  const storage: StorageLike | null = draftBackup
    ? (draftBackup.storage ?? (typeof localStorage !== "undefined" ? localStorage : null))
    : null;

  const pendingPatch: Ref<Partial<Patch>> = ref({});
  const isSaving = ref(false);
  const hasPending = computed(() => Object.keys(pendingPatch.value).length > 0);

  const persistDraft = () => {
    if (!storage || !draftBackup) return;
    const key = draftBackup.storageKey();
    if (!hasPending.value) {
      storage.removeItem(key);
      return;
    }
    storage.setItem(key, JSON.stringify({ patch: pendingPatch.value, savedAt: Date.now() }));
  };

  const flushNow = async () => {
    if (!hasPending.value || isSaving.value) return;
    const snapshot = { ...pendingPatch.value };
    pendingPatch.value = {};
    isSaving.value = true;
    try {
      await save(snapshot);
      persistDraft();
    } catch {
      // Re-queue the failed snapshot underneath edits that arrived during the save, so newer keys win the merge.
      pendingPatch.value = { ...snapshot, ...pendingPatch.value };
      persistDraft();
    } finally {
      isSaving.value = false;
    }
    // Edits arrived mid-flight — re-arm the debounce so they get persisted.
    if (hasPending.value) debouncedFlush();
  };

  const debouncedFlush = useDebounceFn(flushNow, debounceMs, { maxWait: maxWaitMs });

  const queue = (patch: Partial<Patch>) => {
    pendingPatch.value = { ...pendingPatch.value, ...patch };
    persistDraft();
    debouncedFlush();
  };

  const restoreDraft = (): Partial<Patch> | null => {
    if (!storage || !draftBackup) return null;
    const key = draftBackup.storageKey();
    const raw = storage.getItem(key);
    if (!raw) return null;
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      storage.removeItem(key);
      return null;
    }
    const envelope = storedDraftEnvelopeSchema.safeParse(parsed);
    if (!envelope.success) {
      storage.removeItem(key);
      return null;
    }
    const { patch, savedAt } = envelope.data;
    if (Date.now() - savedAt > expiryMs) {
      storage.removeItem(key);
      return null;
    }
    const validatedPatch = draftBackup.patchSchema.safeParse(patch);
    if (!validatedPatch.success) {
      storage.removeItem(key);
      return null;
    }
    pendingPatch.value = { ...pendingPatch.value, ...validatedPatch.data };
    debouncedFlush();
    return validatedPatch.data;
  };

  onBeforeRouteLeave(async () => {
    if (hasPending.value) await flushNow();
  });

  onBeforeUnmount(async () => {
    if (hasPending.value) await flushNow();
  });

  const handleBeforeUnload = (event: BeforeUnloadEvent) => {
    if (hasPending.value || isSaving.value) {
      event.preventDefault();
      event.returnValue = "";
    }
  };
  onMounted(() => window.addEventListener("beforeunload", handleBeforeUnload));
  onUnmounted(() => window.removeEventListener("beforeunload", handleBeforeUnload));

  return { queue, restoreDraft };
}
